import { CONFIG } from "@/shared/model/config";
import { useSession } from "@/shared/model/session";

import type {
  NotificationWSMessage,
  SystemWSMessage,
  TicketWSMessage,
} from "./types";

type TicketMessageHandler = (message: TicketWSMessage) => void;
type NotificationMessageHandler = (message: NotificationWSMessage) => void;
type SystemMessageHandler = (message: SystemWSMessage) => void;

// ActionCable message types
type ActionCableCommand = "subscribe" | "unsubscribe" | "message";

type ActionCableMessage = {
  command: ActionCableCommand;
  identifier: string;
  data?: string;
};

// ActionCable received message types
type ActionCableReceivedMessage = {
  type?: "ping" | "welcome" | "confirm_subscription";
  identifier?: string;
  message?: TicketWSMessage | NotificationWSMessage | SystemWSMessage;
};

type ChannelIdentifier = {
  channel: string;
};

class WebSocketClient {
  private ws: WebSocket | null = null;
  private ticketHandlers: Set<TicketMessageHandler> = new Set();
  private notificationHandlers: Set<NotificationMessageHandler> = new Set();
  private systemHandlers: Set<SystemMessageHandler> = new Set();
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private isIntentionallyClosed = false;
  private connectionAttempts = 0;

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isIntentionallyClosed = false;
    this.connectionAttempts++;

    const sessionState = useSession.getState();
    const refreshToken = sessionState.tokens?.refreshToken;

    if (!refreshToken) {
      return;
    }

    const params = new URLSearchParams({
      refresh_token: refreshToken,
    });

    const url = `${CONFIG.WS_URL}/cable?${params.toString()}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.reconnectDelay = 1000;

      // Subscribe to TicketsChannel
      this.send({
        command: "subscribe",
        identifier: JSON.stringify({ channel: "TicketsChannel" }),
      });

      // Subscribe to NotificationsChannel
      this.send({
        command: "subscribe",
        identifier: JSON.stringify({ channel: "NotificationsChannel" }),
      });

      // Subscribe to SystemChannel
      this.send({
        command: "subscribe",
        identifier: JSON.stringify({ channel: "SystemChannel" }),
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const data: ActionCableReceivedMessage = JSON.parse(event.data);

        if (data.type === "ping") {
          return;
        }

        if (data.type === "welcome") {
          return;
        }

        if (data.type === "confirm_subscription") {
          return;
        }

        if (data.message) {
          if (data.identifier) {
            const identifier: ChannelIdentifier = JSON.parse(data.identifier);

            if (identifier.channel === "TicketsChannel") {
              this.ticketHandlers.forEach((handler) => {
                handler(data.message as TicketWSMessage);
              });
            }

            if (identifier.channel === "NotificationsChannel") {
              this.notificationHandlers.forEach((handler) => {
                handler(data.message as NotificationWSMessage);
              });
            }

            if (identifier.channel === "SystemChannel") {
              this.systemHandlers.forEach((handler) => {
                handler(data.message as SystemWSMessage);
              });
            }
          }
        }
      } catch (error) {
        console.error("[websocket] failed to parse message", error);
      }
    };

    this.ws.onerror = (event) => {
      console.error("[websocket] connection error", event);
    };

    this.ws.onclose = () => {
      this.ws = null;

      if (!this.isIntentionallyClosed) {
        this.scheduleReconnect();
      }
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 2,
        this.maxReconnectDelay,
      );
    }, this.reconnectDelay);
  }

  private send(data: ActionCableMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message = JSON.stringify(data);
      this.ws.send(message);
    }
  }

  subscribeToTickets(handler: TicketMessageHandler) {
    this.ticketHandlers.add(handler);

    return () => {
      this.ticketHandlers.delete(handler);
    };
  }

  subscribeToNotifications(handler: NotificationMessageHandler) {
    this.notificationHandlers.add(handler);

    return () => {
      this.notificationHandlers.delete(handler);
    };
  }

  subscribeToSystem(handler: SystemMessageHandler) {
    this.systemHandlers.add(handler);

    return () => {
      this.systemHandlers.delete(handler);
    };
  }

  disconnect() {
    this.isIntentionallyClosed = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.ticketHandlers.clear();
    this.notificationHandlers.clear();
    this.systemHandlers.clear();
  }
}

export const websocketClient = new WebSocketClient();
